import {Component, EventEmitter, Input, Output, WritableSignal, signal, inject, model} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { MatSlideToggle, MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatFormField } from '@angular/material/form-field';
import { MatInput, MatLabel, MatError } from '@angular/material/input';
import { NgClass } from '@angular/common';
import {BlockUI, BlockUIModule, NgBlockUI} from 'ng-block-ui';
import { MemberAssignmentToStandardsComponent } from '../member-assignment-to-standards/member-assignment-to-standards.component';
import { first } from 'rxjs/operators';
import {WeaveFormsModule} from "../../../../../shared/forms/weave-forms.module";
import {ApiCallService} from "../../../../../services/api-call.service"
import {ShortWeaveLoaderComponent} from "../../../../../shared/loaders/short-weave-loader/short-weave-loader.component";
import {teamMember} from "../team-management.component";
import {Session} from "../../../../../services/session.service";

interface standard {
  checked: boolean;
  description: string;
  name: string;
  requestKey: string;
}

interface standardsGroup {
  description: string;
  intermediate: boolean;
  name: string;
  requestKey: string;
  standards: standard[];
  checked: boolean;
}

@Component({
  selector: 'app-add-team-member',
  standalone: true,
  templateUrl: './add-team-member.component.html',
  styleUrls: ['../team-management.component.scss'],
  imports: [
    FormsModule,
    CdkTextareaAutosize,
    MatFormField,
    MatInput,
    MatLabel,
    MatError,
    MatSlideToggle,
    NgClass,
    WeaveFormsModule,
    MemberAssignmentToStandardsComponent,
    BlockUIModule,
  ]
})
export class AddTeamMemberComponent{
  private readonly apiService: ApiCallService = inject(ApiCallService);
  private readonly session = inject(Session);

  @Input() groups: standardsGroup[] = [];
  members = model.required<teamMember[]>();

  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  @BlockUI('teamManagementUI') blockUI: NgBlockUI;
  protected readonly blockTemplate = ShortWeaveLoaderComponent;

  // Local state moved from parent
  fullAccess: boolean = true;
  emailList: string = '';
  memberGroups: WritableSignal<standardsGroup[]> = signal<standardsGroup[]>([]);
  canSave: WritableSignal<boolean> = signal(false);
  errorMessage: WritableSignal<string> = signal('');
  emailValidationMessage: WritableSignal<string> = signal('');

  ngOnChanges() {
    // Initialize memberGroups when input groups change
    if (this.groups && this.groups.length) {
      // Deep clone to avoid mutating parent input
      const cloned = this.groups.map(g => ({
        ...g,
        standards: g.standards.map(s => ({ ...s }))
      }));
      this.memberGroups.set(cloned);
    }
  }

  toggleFullAccess(fullAccessEvent: MatSlideToggleChange) {
    if (fullAccessEvent.checked) {
      this.memberGroups.update(groups => {
        groups.forEach(group => {
          group.checked = true;
          group.standards.forEach(standard => (standard.checked = true));
        });
        return [...groups];
      });
    }
  }

  private isValidEmailList(input: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return input
      .split(',')
      .map(email => email.trim())
      .every(email => email.length > 0 && emailRegex.test(email));
  }

  checkCanSave() {
    this.emailValidationMessage.set('');
    const canSave = !!this.emailList && this.emailList.length > 0 && this.isValidEmailList(this.emailList);
    this.canSave.set(canSave);
    if (this.emailList && this.emailList.length > 0 && !this.canSave()) {
      this.emailValidationMessage.set('Please enter a comma-separated list of valid email addresses.');
    }
  }

  onGroupsChange(groups: standardsGroup[]) {
    this.memberGroups.set(groups);
  }

  saveMembersList() {
    this.errorMessage.set('');
    let emails: string[] = this.emailList
      .split(',')
      .map(email => email.trim())
      .filter(email => email.length > 0);
    emails = [...new Set(emails)];

    const projects = this.memberGroups()
      .flatMap(group => group.standards.filter(standard => standard.checked).map(standard => standard.requestKey));

    const newMembers = emails.map(email => ({ email, projects }));

    this.blockUI.start();
    this.apiService
      .postJsonData('/inviteAccountUsers/', { newMembers })
      .pipe(first())
      .subscribe(response => {
        this.blockUI.stop();
        if (response && response.success) {
          this.reloadMembers(response.data[0].dataSet);
          this.saved.emit();
        } else {
          if (response && response.dataError) {
            this.errorMessage.set(response.dataError[0].description);
          } else {
            this.errorMessage.set(
              'There was a problem with inviting members. The server did not return any error messages. Please contact support.'
            );
          }
        }
      });
  }

  reloadMembers(members: any){
    const newMembersRemote: any[] = members.filter(member => !this.members().find(m => m.requestKey === member.requestKey));
    let newMembers: teamMember[] = [];
    newMembersRemote
      .filter( member => member.requestKey !== this.session.getUser().userRequestKey)
      .forEach(member => {
      if(!member.name) { // The  user may not have a name set yet, they have been just added to the team
        member.name = member.email;
      }
      const names = member.name ? member.name.split( ' ' ) : [ '', '' ];
      const firstName = names[ 0 ] || '';
      const lastName = names.slice( 1 ).join( ' ' ) || '';
      const teamMember: teamMember = {
        name: member.name,
        firstName: firstName,
        lastName: lastName,
        requestKey: member.requestKey,
        avatar: member.avatar ? member.avatar : '',
        userRole: member.accountRoleId,
        pending: member.pending,
        email: member.email,
        inviteDate: member.inviteDate ? new Date(member.inviteDate) : null,
      };
      newMembers.push(teamMember);
    });
    this.members.update(existing => [...existing, ...newMembers]);
    this.session.setAccountStatusUsers(
      this.members().map(member => ({
        name: member.name,
        email: member.email,
        pending: member.pending,
      })).concat([{
        name: this.session.getUser().name,
        email: this.session.getUser().email,
        pending: false,
      }])
    );
  }

  cancel() {
    this.closed.emit();
  }
}
